// src/components/DemocracyIndicesTab.jsx
import React, { useState, useRef, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Check, Upload, Trash2, RefreshCw } from 'lucide-react';

const DemocracyIndicesTab = () => {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Fetch indices data on component mount
  useEffect(() => {
    fetchIndices();
    fetchDataSources();
  }, []);
  
  // State for data sources
  const [dataSources, setDataSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  
  // Function to fetch data sources
  const fetchDataSources = async () => {
    try {
      const response = await fetch('/api/admin/indices/sources');
      
      if (!response.ok) {
        console.warn(`Failed to fetch data sources: ${response.status}`);
        // Try fallback endpoint
        const fallbackResponse = await fetch('/api/admin/data-sources');
        if (!fallbackResponse.ok) {
          throw new Error(`Failed to fetch data sources: ${fallbackResponse.status}`);
        }
        const data = await fallbackResponse.json();
        setDataSources(data);
        return;
      }
      
      const data = await response.json();
      setDataSources(data);
    } catch (err) {
      console.error('Error fetching data sources:', err);
      // Don't set error state here as it's not critical
    }
  };
  
  // State for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Function to fetch indices data with pagination
  const fetchIndices = async (pageNum = 1, limit = pageSize) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/admin/indices?page=${pageNum}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch indices: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Check if the response is already paginated (new format)
      if (result.data && result.pagination) {
        console.log(`Fetched ${result.data.length} indices (page ${result.pagination.page}/${result.pagination.totalPages})`);
        setIndices(result.data);
        setTotalRecords(result.pagination.totalCount);
        setTotalPages(result.pagination.totalPages);
        setPage(result.pagination.page);
        setPageSize(result.pagination.limit);
      } else {
        // Handle legacy format (unpaginated array)
        console.log('Fetched indices data (legacy format):', result.length > 0 ? result[0] : 'No records');
        setIndices(result);
        setTotalRecords(result.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching democracy indices:', err);
      setError('Failed to load democracy indices data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
    setUploadStatus(null);
  };
  
  // Handle file upload
  const handleFileUpload = async () => {
    if (!importFile) {
      setUploadStatus({
        success: false,
        message: 'Please select a file first'
      });
      return;
    }
    
    setUploadStatus({
      success: null,
      message: 'Uploading...'
    });
    
    try {
      // Parse file content
      const fileContent = await importFile.text();
      const lines = fileContent.split('\n');
      
      // Basic validation
      if (lines.length < 2) {
        throw new Error('File does not contain enough data');
      }
      
      // Parse header
      const header = lines[0].split(',');
      if (!header.includes('pillar_id') || !header.includes('year') || !header.includes('score')) {
        throw new Error('File is missing required columns (pillar_id, year, score)');
      }
      
      // Prepare data for API
      const dataRows = lines.slice(1).filter(line => line.trim() !== '');
      
      // Upload data
      const response = await fetch('/api/admin/indices/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: dataRows.map(row => {
            const values = row.split(',');
            const dataObj = {};
            header.forEach((col, index) => {
              dataObj[col] = values[index];
            });
            return dataObj;
          })
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
      
      const result = await response.json();
      
      setUploadStatus({
        success: true,
        message: `Successfully imported ${result.imported} records and updated ${result.updated} records`,
        details: result
      });
      
      // Refresh data
      fetchIndices();
      
    } catch (err) {
      console.error('Error uploading data:', err);
      setUploadStatus({
        success: false,
        message: `Upload failed: ${err.message}`
      });
    }
  };

  // Handle removing duplicates
  const handleRemoveDuplicates = async () => {
    if (removingDuplicates) return;
    
    setRemovingDuplicates(true);
    setDuplicateResults(null);
    
    try {
      // Phase 1: Analyze database
      const analysisResponse = await fetch('/api/admin/remove-duplicates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!analysisResponse.ok) {
        const errorData = await analysisResponse.json();
        throw new Error(errorData.error || 'Failed to analyze database');
      }
      
      const analysisResult = await analysisResponse.json();
      
      // If analysis was successful, proceed to phase 2
      if (analysisResult.success) {
        // Check if we have duplicates to remove
        const legacyDuplicates = analysisResult.results.legacy_table.duplicate_groups || 0;
        const enhancedDuplicates = analysisResult.results.enhanced_table.duplicate_groups || 0;
        const totalDuplicateGroups = legacyDuplicates + enhancedDuplicates;
        
        if (totalDuplicateGroups > 0) {
          // We found duplicates, proceed to removal phase
          const removalResponse = await fetch('/api/admin/remove-duplicates-phase2', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          if (!removalResponse.ok) {
            throw new Error('Failed to remove duplicates');
          }
          
          const removalResult = await removalResponse.json();
          
          // Combine analysis and removal results
          setDuplicateResults({
            success: true,
            message: 'Successfully analyzed database and removed duplicates',
            analysis: analysisResult.results,
            removal: removalResult.results
          });
        } else {
          // No duplicates found
          setDuplicateResults({
            success: true,
            message: 'No duplicate records found',
            analysis: analysisResult.results
          });
        }
      } else {
        // Analysis wasn't successful
        setDuplicateResults(analysisResult);
      }
      
      // Refresh data
      fetchIndices();
      
    } catch (err) {
      console.error('Error removing duplicates:', err);
      setDuplicateResults({
        success: false,
        message: `Error: ${err.message}`
      });
    } finally {
      setRemovingDuplicates(false);
    }
  };
  
  // Group indices by year for visualization
  const getChartData = () => {
    if (!indices || indices.length === 0) {
      return [];
    }
    
    // Get unique years and sort
    const years = [...new Set(indices.map(item => item.year))];
    years.sort((a, b) => a - b);
    
    // Get most recent 5 years
    const recentYears = years.slice(-5);
    
    // Calculate average score by year
    return recentYears.map(year => {
      const yearData = indices.filter(item => item.year === year);
      const avgScore = yearData.reduce((sum, item) => sum + item.score, 0) / yearData.length;
      
      return {
        year,
        score: avgScore.toFixed(2)
      };
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Democracy Indices Management</h2>
      
      {/* Upload Section */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-medium mb-2">Import Indices Data</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a CSV file with democracy indices data. The file should include columns for pillar_id, year, score, source_name, category, and country_code.
        </p>
        
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100 transition-colors flex items-center"
          >
            <Upload size={16} className="mr-2" />
            Select CSV File
          </button>
          
          {importFile && (
            <>
              <span className="text-sm text-gray-700">
                {importFile.name} ({Math.round(importFile.size / 1024)} KB)
              </span>
              
              <button
                onClick={handleFileUpload}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Import Data
              </button>
            </>
          )}
        </div>
        
        {uploadStatus && (
          <div className={`p-3 rounded text-sm ${
            uploadStatus.success === true ? 'bg-green-50 text-green-700 border border-green-200' :
            uploadStatus.success === false ? 'bg-red-50 text-red-700 border border-red-200' :
            'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <div className="flex items-center">
              {uploadStatus.success === true ? (
                <Check size={16} className="mr-2 text-green-500" />
              ) : uploadStatus.success === false ? (
                <AlertTriangle size={16} className="mr-2 text-red-500" />
              ) : (
                <div className="mr-2 w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{uploadStatus.message}</span>
            </div>
            
            {uploadStatus.success === true && uploadStatus.details && (
              <div className="mt-2 text-xs">
                <div>Imported: {uploadStatus.details.imported}</div>
                <div>Updated: {uploadStatus.details.updated}</div>
                <div>Skipped: {uploadStatus.details.skipped}</div>
                <div>Errors: {uploadStatus.details.errors}</div>
              </div>
            )}
          </div>
        )}
        
        {/* Remove Duplicates Button */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-md font-medium mb-2">Database Maintenance</h4>
          <button
            onClick={handleRemoveDuplicates}
            disabled={removingDuplicates}
            className="px-4 py-2 bg-amber-50 text-amber-700 rounded border border-amber-200 hover:bg-amber-100 transition-colors flex items-center"
          >
            {removingDuplicates ? (
              <RefreshCw size={16} className="mr-2 animate-spin" />
            ) : (
              <Trash2 size={16} className="mr-2" />
            )}
            Remove Duplicate Indices
          </button>
          
          {duplicateResults && (
            <div className={`mt-3 p-3 rounded text-sm ${
              duplicateResults.success ? 'bg-green-50 text-green-700 border border-green-200' :
              'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <div className="flex items-center">
                {duplicateResults.success ? (
                  <Check size={16} className="mr-2 text-green-500" />
                ) : (
                  <AlertTriangle size={16} className="mr-2 text-red-500" />
                )}
                <span>{duplicateResults.message}</span>
              </div>
              
              {duplicateResults.success && (
                <div className="mt-2 text-xs">
                  {/* Analysis Results */}
                  {duplicateResults.analysis && (
                    <div className="mb-2">
                      <div className="font-medium mb-1">Analysis Results:</div>
                      <div>
                        <strong>Legacy Table:</strong> {duplicateResults.analysis.legacy_table.count} records, 
                        {duplicateResults.analysis.legacy_table.duplicate_groups} duplicate groups
                      </div>
                      
                      <div>
                        <strong>Enhanced Table:</strong> {duplicateResults.analysis.enhanced_table.count} records, 
                        {duplicateResults.analysis.enhanced_table.duplicate_groups} duplicate groups
                      </div>
                    </div>
                  )}
                  
                  {/* Removal Results */}
                  {duplicateResults.removal && (
                    <div>
                      <div className="font-medium mb-1">Removal Results:</div>
                      {duplicateResults.removal.legacy_table.processed && (
                        <div>
                          <strong>Legacy Table:</strong> Removed {duplicateResults.removal.legacy_table.removed} duplicate records
                        </div>
                      )}
                      
                      {duplicateResults.removal.enhanced_table.processed && (
                        <div>
                          <strong>Enhanced Table:</strong> Removed {duplicateResults.removal.enhanced_table.removed} duplicate records
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Data Sources Section */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-4">Data Sources Management</h3>
        
        {dataSources.length > 0 ? (
          <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataSources.map((source) => (
                  <tr key={source.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{source.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{source.organization}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {source.url && (
                        <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View Source
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => setSelectedSource(source)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        Edit
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete the source "${source.name}"?`)) {
                            // Handle delete functionality
                            alert('Delete functionality will be implemented');
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-md text-center text-gray-500">
            No data sources available. Add a new source to get started.
          </div>
        )}
        
        <div className="mt-4">
          <button
            onClick={() => setSelectedSource({})} // Empty object for new source
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
          >
            Add New Data Source
          </button>
        </div>
        
        {/* Source Edit Modal (placeholder) */}
        {selectedSource && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
              <h3 className="text-lg font-medium mb-4">
                {selectedSource.id ? `Edit Source: ${selectedSource.name}` : 'Add New Data Source'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    defaultValue={selectedSource.name || ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    defaultValue={selectedSource.organization || ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input 
                    type="url" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    defaultValue={selectedSource.url || ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    defaultValue={selectedSource.description || ''}
                    rows="3"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setSelectedSource(null)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Handle save functionality
                      alert('Save functionality will be implemented');
                      setSelectedSource(null);
                      fetchDataSources();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Data Visualization */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-4">Recent Trends Overview</h3>
        
        {loading ? (
          <div className="py-8 flex justify-center items-center text-gray-500">
            <div className="animate-spin h-5 w-5 border-2 border-gray-500 rounded-full border-t-transparent mr-2"></div>
            Loading...
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md">
            <div className="flex items-center">
              <AlertTriangle size={18} className="mr-2" />
              {error}
            </div>
          </div>
        ) : indices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No democracy indices data available. Use the import function to add data.
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getChartData()}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis domain={[0, 10]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" name="Democracy Score" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      
      {/* Data Summary */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-4">Data Summary</h3>
        
        {loading ? (
          <div className="py-4 text-gray-500">Loading...</div>
        ) : error ? (
          <div className="py-4 text-red-500">{error}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-md">
              <div className="text-blue-800 font-medium">Total Records</div>
              <div className="text-2xl">{indices.length}</div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-md">
              <div className="text-purple-800 font-medium">Years Covered</div>
              <div className="text-2xl">
                {indices.length > 0 ? 
                  `${Math.min(...indices.map(i => i.year))} - ${Math.max(...indices.map(i => i.year))}` : 
                  'None'}
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-md">
              <div className="text-green-800 font-medium">Data Sources</div>
              <div className="text-2xl">
                {indices.length > 0 ? 
                  new Set(indices.map(i => i.source_name)).size : 
                  '0'}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Raw Data Browser */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-4">Democracy Indices Data Browser</h3>
        
        {loading ? (
          <div className="py-4 text-gray-500">Loading...</div>
        ) : error ? (
          <div className="py-4 text-red-500">{error}</div>
        ) : indices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No democracy indices data available. Use the import function to add data.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white shadow-sm border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pillar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {indices.map((index, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{index.pillar_name || `Pillar ${index.pillar_id}`}</td>
                    <td className="px-4 py-3 text-sm">{index.year}</td>
                    <td className="px-4 py-3 text-sm font-medium">{index.score.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">
                      {index.change_percentage ? (
                        <span className={
                          index.change_percentage > 0 
                            ? "text-green-600" 
                            : index.change_percentage < 0 
                              ? "text-red-600" 
                              : "text-gray-600"
                        }>
                          {index.change_percentage > 0 ? '+' : ''}
                          {index.change_percentage.toFixed(2)}%
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">{index.source_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <button 
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                        onClick={() => alert(`Edit functionality for index ID ${index.id} will be implemented`)}
                      >
                        Edit
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this record?')) {
                            alert(`Delete functionality for index ID ${index.id} will be implemented`);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indices.length}</span> records
                    {totalRecords > 0 && (
                      <> (page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>)</>
                    )}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchIndices(page > 1 ? page - 1 : 1)}
                    disabled={page <= 1}
                    className={`px-4 py-2 border rounded-md text-sm font-medium ${
                      page <= 1 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => fetchIndices(page < totalPages ? page + 1 : totalPages)}
                    disabled={page >= totalPages}
                    className={`px-4 py-2 border rounded-md text-sm font-medium ${
                      page >= totalPages 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemocracyIndicesTab;